/**
 * Format phone number to Canadian/US format: 5551234567 -> 555-123-4567
 * @param {string} phone - Raw phone number
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // If it's 10 digits, format as XXX-XXX-XXXX
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  }
  
  // If it's 11 digits and starts with 1, format as 1-XXX-XXX-XXXX
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4');
  }
  
  // Return as-is if not a standard format
  return phone;
};

/**
 * Format Canadian postal code: H1H2H2 -> H1H 2H2
 * @param {string} postalCode - Raw postal code
 * @returns {string} Formatted postal code
 */
export const formatPostalCode = (postalCode) => {
  // Remove all spaces and convert to uppercase
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
  
  // If it's 6 characters, format as XXX XXX
  if (cleaned.length === 6) {
    return cleaned.replace(/([A-Z]\d[A-Z])(\d[A-Z]\d)/, '$1 $2');
  }
  
  // Return as-is if not 6 characters
  return postalCode;
};

/**
 * Clean phone number for backend (remove formatting)
 * @param {string} phone - Formatted phone number
 * @returns {string} Clean phone number (digits only)
 */
export const cleanPhoneNumber = (phone) => {
  return phone.replace(/\D/g, '');
};

/**
 * Clean postal code for backend (remove spaces, convert to uppercase)
 * @param {string} postalCode - Formatted postal code
 * @returns {string} Clean postal code
 */
export const cleanPostalCode = (postalCode) => {
  return postalCode.replace(/\s/g, '').toUpperCase();
};

/**
 * Validate Canadian postal code format
 * @param {string} postalCode - Postal code to validate
 * @returns {boolean} True if valid format
 */
export const isValidPostalCode = (postalCode) => {
  const pattern = /^[A-Z]\d[A-Z] \d[A-Z]\d$/;
  return pattern.test(postalCode);
};

/**
 * Validate Canadian/US phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid format
 */
export const isValidPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
};
